import { useEffect } from 'preact/hooks';

/**
 * 353: the booking page read the booking once, so a customer who opened it before the window kept seeing "Your
 * lockbox code appears at 09:45" — and no location link — until they reloaded. The server decides what is shown
 * (`bookings/lockbox.ts`, `tracking.ts`: a paid booking from 15 minutes before pickup until the return time); this
 * only asks it again at the moments that answer can change, and whenever the app comes back to the foreground.
 */
export const WINDOW_LEAD_MS = 15 * 60 * 1000;
/** A timer cannot wait longer than this (2^31 − 1 ms, about 24.8 days); a later edge is re-armed on the next read. */
const MAX_TIMER_MS = 2_147_483_647;
/** A little after the edge, so the server's clock has passed it too. */
const SETTLE_MS = 2_000;

export interface WindowBooking {
  status: string;
  pickup_at?: string | null;
  return_at?: string | null;
}

/** The next instant the window opens or closes for this booking, or null when nothing is coming. */
export function nextWindowEdge(b: WindowBooking, now: number): number | null {
  if (b.status !== 'confirmed' && b.status !== 'checked_out') return null;
  const edges = [
    b.pickup_at ? new Date(b.pickup_at).getTime() - WINDOW_LEAD_MS : Number.NaN,
    b.return_at ? new Date(b.return_at).getTime() : Number.NaN,
  ].filter((t) => Number.isFinite(t) && t > now);
  return edges.length > 0 ? Math.min(...edges) : null;
}

export function useWindowRefresh(
  booking: WindowBooking | null | undefined,
  refresh: () => void,
): void {
  const edge = booking ? nextWindowEdge(booking, Date.now()) : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-armed only when the edge itself moves
  useEffect(() => {
    if (edge === null) return;
    const timer = setTimeout(refresh, Math.min(edge - Date.now() + SETTLE_MS, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [edge]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener for the page's life
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
}
